import { ArmyList, Nullable } from '@Models';
import { Result } from '@Redux/type';
import { TYPES } from '@Types';
import { container } from '@Container';
import { RequestAction, RequestFailure } from '@Repository/RequestAction';
import {
  ArmyListRepository,
  RequestArmyListsSuccess,
  SearchRepository,
} from '@Repository/builder';
import { inject, injectable } from 'inversify';
import myBuilderSlice from '@Redux/reducers/builder/myBuilder.slice';
import { MyBuilderActionState } from '@Stores/builder/MyBuilderState';
import { PayloadAction } from '@reduxjs/toolkit';
import { IEntitiesAction } from '@Redux/IEntitiesAction';
import armyListsSlice from '@Redux/reducers/builder/armyLists.slice';

@injectable()
export class ArmyListsAction extends IEntitiesAction<
  ArmyList,
  RequestArmyListsSuccess
> {
  @inject(TYPES.Repository.ArmyListRepository)
  private _repository!: ArmyListRepository;

  // TODO : A tester
  public searchArmyLists =
    ({
      armyId,
      min,
      max,
    }: {
      armyId: number;
      min: number;
      max: number;
    }): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const repository = container.get<SearchRepository>(
        TYPES.Repository.SearchRepository,
      );

      const response = await repository.searchArmyList({
        armyId: armyId,
        min: min,
        max: max,
      });

      if (response instanceof RequestFailure) {
        const failure = response as RequestFailure;

        dispatch(this.haveError(failure.error.message));
        dispatch(this.areLoading(false));
        return;
      }

      const success = response as RequestArmyListsSuccess;
      dispatch(this.fetchDataSuccess(success.payload));
      dispatch(this.areLoading(false));
    };

  public setAction(action: string): PayloadAction<MyBuilderActionState> {
    switch (action) {
      case 'REQUEST_CREATION_ARMY_LIST':
        return myBuilderSlice.actions.setRequest(
          MyBuilderActionState.ArmyListCreation,
        );
      case 'REQUEST_FORMAT_ARMY_LIST':
        return myBuilderSlice.actions.setRequest(
          MyBuilderActionState.ArmyListFormat,
        );
      case 'REQUEST_CREATION_FOLDER':
        return myBuilderSlice.actions.setRequest(
          MyBuilderActionState.FolderCreation,
        );
      case 'REQUEST_CREATION_BOOK':
        return myBuilderSlice.actions.setRequest(
          MyBuilderActionState.BookCreation,
        );
      case 'REQUEST_CREATION_CONTEST':
        return myBuilderSlice.actions.setRequest(
          MyBuilderActionState.ContestCreation,
        );
      case 'REQUEST_CREATION_ARSENAL_ARMY':
        return myBuilderSlice.actions.setRequest(
          MyBuilderActionState.ArsenalArmyCreation,
        );
    }
    return { type: 'no-action' } as PayloadAction<MyBuilderActionState>;
  }

  public fetchDataSuccess(item: ArmyList[]): PayloadAction<ArmyList[]> {
    return armyListsSlice.actions.setArmyLists(item);
  }

  protected cacheDataSuccess(item: ArmyList[]): PayloadAction<ArmyList[]> {
    return armyListsSlice.actions.setArmyLists(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return armyListsSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return armyListsSlice.actions.setLoading(bool);
  }

  protected async getItems(): Promise<RequestAction> {
    return this._repository.getArmyLists({ locale: null });
  }

  protected async getCachedItems(): Promise<Nullable<Array<ArmyList>>> {
    return this._repository.getAllCached({ locale: null });
  }
}
